import React from "react";
import { FiAlertCircle, FiTrash2, FiX } from "react-icons/fi";
import "./style.css";

const focusableSelector = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const ConfirmationDialog = ({
  open,
  title,
  description,
  confirmLabel = "Șterge",
  cancelLabel = "Păstrează",
  isConfirming = false,
  onConfirm,
  onCancel,
}) => {
  const dialogRef = React.useRef(null);
  const cancelButtonRef = React.useRef(null);
  const previousFocusRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current = document.activeElement;
    cancelButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isConfirming) {
        event.preventDefault();
        onCancel();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusableElements = Array.from(
        dialogRef.current.querySelectorAll(focusableSelector)
      );
      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, [isConfirming, onCancel, open]);

  if (!open) return null;

  return (
    <div
      className="confirmation-dialog"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isConfirming) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        className="confirmation-dialog__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
        aria-busy={isConfirming}
      >
        <button
          type="button"
          className="confirmation-dialog__close"
          onClick={onCancel}
          disabled={isConfirming}
          aria-label="Închide dialogul"
        >
          <FiX aria-hidden="true" />
        </button>

        <div className="confirmation-dialog__icon" aria-hidden="true">
          <FiAlertCircle />
        </div>
        <span className="confirmation-dialog__eyebrow">CONFIRMARE NECESARĂ</span>
        <h2 id="confirmation-dialog-title">{title}</h2>
        <p id="confirmation-dialog-description">{description}</p>

        <div className="confirmation-dialog__actions">
          <button
            ref={cancelButtonRef}
            type="button"
            className="confirmation-dialog__button confirmation-dialog__button--cancel"
            onClick={onCancel}
            disabled={isConfirming}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="confirmation-dialog__button confirmation-dialog__button--confirm"
            onClick={onConfirm}
            disabled={isConfirming}
          >
            <FiTrash2 aria-hidden="true" />
            {isConfirming ? "Se șterge…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
